import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, Observable, switchMap } from 'rxjs';
import { actionTrackerContext, oeeContext } from '../../../core/interceptors/api-interceptor.interceptor';
import { ActionTrackerService } from '../../../core/services/action-tracker.service';
import { IBoardItemConfiguration } from '../../../pages/dashboard-content/widget-add-edit-dialog/widget-add-edit-dialog.interface';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IShiftDetails } from '../advanced-filter/advanced-filter-api-matcher/shift-advanced-filter-api-matcher';
import { AdvancedFilterPredefinedData } from '../advanced-filter/advanced-filter-predefined-data';
import { IDropdownOption } from '../mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../mat-dropdown/scw-mat-dropdown.interface';
import { IOptionSelection, TStatusCategory } from './item-category-shift-configuration.interface';

export interface IItemCategoryShiftConfigurationState {
  errorStatus: number | null;
  shifts: IDropdownOption<number>[];
  shiftsStatus: ELoadStatus;
  totalShift: number;
  itemCategories: IDropdownOption<number>[];
  itemCategoriesStatus: ELoadStatus;
  totalItemCategory: number;
}

@Injectable()
export class ItemCategoryShiftConfigurationStore extends ComponentStore<IItemCategoryShiftConfigurationState> {
  readonly itemCategoryDropdown$ = this.select((state) => state.itemCategories);
  readonly shiftsDropdown$ = this.select((state) => state.shifts);
  readonly shiftsStatus$ = this.select((state) => state.shiftsStatus);
  readonly itemCategoriesStatus$ = this.select((state) => state.itemCategoriesStatus);

  readonly loadItemCategories = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        this.patchState({
          errorStatus: null,
          itemCategories: [],
          itemCategoriesStatus: ELoadStatus.loading,
        });

        return this.getItemCategories(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IBoardItemConfiguration>) => {
              this.patchState({
                errorStatus: null,
                itemCategories: ActionTrackerService.buildCategoryDropdownOptions(response.data, this.translate),
                itemCategoriesStatus: ELoadStatus.success,
                totalItemCategory: response.total,
              });
            },
            (error: { status: number }) => {
              this.patchState({
                errorStatus: error?.status ?? 500,
                itemCategoriesStatus: ELoadStatus.failure,
              });
            },
          ),
          catchError((error: { status: number }) => {
            this.patchState({
              errorStatus: error?.status ?? 500,
              itemCategoriesStatus: ELoadStatus.failure,
            });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  readonly loadShifts = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        this.patchState({
          errorStatus: null,
          shifts: [],
          shiftsStatus: ELoadStatus.loading,
        });

        return this.getShift(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IShiftDetails>) => {
              this.patchState({
                errorStatus: null,
                shifts: response.data.map((shift) => ({ id: shift.id, name: shift.name })),
                shiftsStatus: ELoadStatus.success,
                totalShift: response.total,
              });
            },
            (error: { status: number }) => {
              this.patchState({
                errorStatus: error?.status ?? 500,
                shiftsStatus: ELoadStatus.failure,
              });
            },
          ),
          catchError((error: { status: number }) => {
            this.patchState({
              errorStatus: error?.status ?? 500,
              shiftsStatus: ELoadStatus.failure,
            });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
    private readonly translate: TranslateService,
    private readonly predefinedData: AdvancedFilterPredefinedData,
  ) {
    super({
      errorStatus: null,
      itemCategories: [],
      itemCategoriesStatus: ELoadStatus.initial,
      shifts: [],
      shiftsStatus: ELoadStatus.initial,
      totalItemCategory: 0,
      totalShift: 0,
    });
  }

  private getShift(params: HttpParams): Observable<IGetManyResponse<IShiftDetails>> {
    return this.http.get<IGetManyResponse<IShiftDetails>>('scheduler/shifts', { context: oeeContext, params });
  }

  private getItemCategories(params: HttpParams): Observable<IGetManyResponse<IBoardItemConfiguration>> {
    return this.http.get<IGetManyResponse<IBoardItemConfiguration>>('board-item-configurations', {
      context: actionTrackerContext,
      params,
    });
  }

  public getShiftDropdownSettings(): IDropdownSettings {
    return {
      enableSearchFilter: true,
      isClientSide: true,
      repositionDropdownList: true,
      singleSelection: false,
      text: this.translate.instant('page.widgetContent.addEditDialog.shift') as string,
    };
  }

  public getItemCategoryDropdownSettings(): IDropdownSettings {
    return {
      enableSearchFilter: true,
      isClientSide: true,
      repositionDropdownList: true,
      singleSelection: true,
      text: this.translate.instant('page.widgetContent.addEditDialog.itemCategory') as string,
    };
  }

  public getOptionDropdownSettings(): IDropdownSettings {
    return {
      enableSearchFilter: true,
      isClientSide: true,
      repositionDropdownList: true,
      singleSelection: true,
      text: this.translate.instant('page.widgetContent.addEditDialog.shiftSelection') as string,
    };
  }

  public getRedStatusCategoryDropdownSettings(): IDropdownSettings {
    return {
      enableSearchFilter: false,
      isClientSide: true,
      repositionDropdownList: true,
      singleSelection: false,
      text: this.translate.instant('page.widgetContent.addEditDialog.redStatusCategories') as string,
    };
  }

  /** The same three status categories the `statusCategory` filter offers, so both selections stay in step. */
  public getRedStatusCategoryOptions(): IDropdownOption<TStatusCategory>[] {
    return this.predefinedData.getOption('statusCategory') as IDropdownOption<TStatusCategory>[];
  }

  public getShiftOptions(): IOptionSelection[] {
    return [
      { count: 0, id: 0, name: this.translate.instant('widget.sqcdpMonthly.shiftSelection.noShift') },
      { count: 1, id: 1, name: this.translate.instant('widget.sqcdpMonthly.shiftSelection.shift1') },
      { count: 2, id: 2, name: this.translate.instant('widget.sqcdpMonthly.shiftSelection.shift2') },
      { count: 3, id: 3, name: this.translate.instant('widget.sqcdpMonthly.shiftSelection.shift3') },
    ];
  }
}
